/**
 * The report you get when a distribution does not go perfectly.
 *
 * Two different kinds of failure, and conflating them would be a mistake:
 *
 *   REJECTED  — the line never left the building. A bad address, a duplicate,
 *               an amount with more decimals than the mint has. Nothing was
 *               sent and nothing was spent. These are caught before a single
 *               transaction is built.
 *   FAILED    — the transaction was built and did not succeed. The recipient
 *               is fine; the send is not. These are the ones to retry.
 *
 * Pure, so the console, the CLI and the API all produce byte-identical reports
 * and the rendering can be tested without any of them.
 */

export interface RejectedLine {
  line: number;
  text: string;
  reason: string;
}

export interface FailedSend {
  line: number;
  address: string;
  amountRaw: string;
  error: string;
}

export interface FailureReport {
  version: 1;
  generatedAt: string;
  cluster: string;
  mint: string;
  symbol?: string;
  /** Who ran it, when known. */
  wallet?: string;
  totalLines: number;
  sent: number;
  rejected: RejectedLine[];
  failed: FailedSend[];
}

export function buildFailureReport(args: {
  cluster: string;
  mint: string;
  symbol?: string;
  wallet?: string;
  totalLines: number;
  sent: number;
  rejected: RejectedLine[];
  failed: FailedSend[];
  now?: Date;
}): FailureReport {
  return {
    version: 1,
    generatedAt: (args.now ?? new Date()).toISOString(),
    cluster: args.cluster,
    mint: args.mint,
    symbol: args.symbol,
    wallet: args.wallet,
    totalLines: args.totalLines,
    sent: args.sent,
    rejected: [...args.rejected].sort((a, b) => a.line - b.line),
    failed: [...args.failed].sort((a, b) => a.line - b.line),
  };
}

export function hasFailures(r: FailureReport): boolean {
  return r.rejected.length > 0 || r.failed.length > 0;
}

const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * CSV, because the thing anyone actually does with this is open it, fix the
 * addresses, and re-import it. The columns match the import format so the
 * corrected file can be fed straight back in.
 */
export function failureCsv(r: FailureReport): string {
  const rows = [["Line", "Outcome", "Wallet Address", "Amount (raw)", "Reason"].join(",")];
  for (const x of r.rejected) {
    rows.push([x.line, "REJECTED — nothing sent", "", "", x.reason].map(csvCell).join(","));
  }
  for (const x of r.failed) {
    rows.push([x.line, "FAILED — send did not succeed", x.address, x.amountRaw, x.error].map(csvCell).join(","));
  }
  return rows.join("\n") + "\n";
}

export function reportFilename(r: FailureReport, ext: string): string {
  const stamp = r.generatedAt.replace(/[:.]/g, "-").replace(/Z$/, "");
  return `distribution-failures-${r.symbol || r.mint.slice(0, 8)}-${r.cluster}-${stamp}.${ext}`;
}

/**
 * The email body.
 *
 * Written to be read on a phone by somebody who has just been told something
 * went wrong: what happened, what it means, what to do, and the numbers. No
 * links to anything that requires signing in to understand the situation.
 */
export function failureEmail(r: FailureReport): { subject: string; text: string } {
  const label = r.symbol ? `${r.symbol} (${r.mint})` : r.mint;
  const subject =
    r.failed.length > 0
      ? `oxMinter: ${r.failed.length} transfer(s) failed distributing ${r.symbol || r.mint.slice(0, 8)}`
      : `oxMinter: ${r.rejected.length} recipient(s) rejected before distributing ${r.symbol || r.mint.slice(0, 8)}`;

  const lines: string[] = [
    `Distribution report — ${r.cluster}`,
    `Token: ${label}`,
    r.wallet ? `Sent from: ${r.wallet}` : "",
    `Generated: ${r.generatedAt}`,
    "",
    `${r.sent} of ${r.totalLines} recipients were paid.`,
    "",
  ].filter(Boolean);

  if (r.rejected.length) {
    lines.push(
      `${r.rejected.length} line(s) were REJECTED before anything was sent.`,
      "These never left your machine. No tokens moved and no fees were spent on them.",
      ""
    );
    for (const x of r.rejected.slice(0, 50)) {
      lines.push(`  line ${x.line}: ${x.reason}`);
    }
    if (r.rejected.length > 50) lines.push(`  …and ${r.rejected.length - 50} more`);
    lines.push("");
  }

  if (r.failed.length) {
    lines.push(
      `${r.failed.length} transfer(s) FAILED after being submitted.`,
      "The addresses are valid; the sends did not succeed. Running the same",
      "distribution again retries exactly these and skips everyone already paid.",
      ""
    );
    for (const x of r.failed.slice(0, 50)) {
      lines.push(`  line ${x.line}: ${x.address} — ${x.error}`);
    }
    if (r.failed.length > 50) lines.push(`  …and ${r.failed.length - 50} more`);
    lines.push("");
  }

  lines.push(
    "The attached CSV lists every one, with the same columns as the import",
    "format, so a corrected file can be fed straight back in.",
    "",
    "Nobody was paid twice: a rerun reconciles against the chain before sending.",
    "",
    "— oxMinter"
  );

  return { subject, text: lines.join("\n") };
}
